/** Visible playfield size. Prefer the visual viewport so mobile chrome does not leave a gap. */
export function viewportSize(): { w: number; h: number } {
  const vv = window.visualViewport;
  if (vv && vv.width > 0 && vv.height > 0) {
    return { w: Math.round(vv.width), h: Math.round(vv.height) };
  }
  return { w: window.innerWidth, h: window.innerHeight };
}

export function isDisplayFullscreen(): boolean {
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  if (document.fullscreenElement || doc.webkitFullscreenElement) return true;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

/** True fullscreen is for phones / tablets. Desktop stays in the window. */
function wantsOsFullscreen(): boolean {
  return window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(hover: none)').matches;
}

/**
 * Ask the browser to hide its chrome. Must run from a user gesture.
 * Android Chrome / Samsung / Firefox usually accept this; iOS Safari always refuses.
 * Desktop / fine-pointer machines skip this so the window is not yanked into FS.
 */
export function requestGameFullscreen(): void {
  if (!wantsOsFullscreen()) return;
  if (isDisplayFullscreen()) return;
  const root = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: (opts?: FullscreenOptions) => Promise<void> | void;
    webkitRequestFullScreen?: () => Promise<void> | void;
  };
  try {
    let result: Promise<void> | void;
    if (typeof root.requestFullscreen === 'function') {
      result = root.requestFullscreen({ navigationUI: 'hide' });
    } else if (typeof root.webkitRequestFullscreen === 'function') {
      result = root.webkitRequestFullscreen({ navigationUI: 'hide' });
    } else if (typeof root.webkitRequestFullScreen === 'function') {
      result = root.webkitRequestFullScreen();
    } else {
      return;
    }
    if (result && typeof result.catch === 'function') result.catch(() => {});
  } catch {
    // iOS Safari and other refusals — standalone Add to Home Screen is the real iOS path.
  }
}

let chromeNudged = false;

/**
 * One-time scroll nudge. On some iOS versions a user-gesture scroll collapses the URL bar.
 * Keeps overflow hidden; does not grow the document. Not true fullscreen.
 */
export function nudgeMobileChrome(): void {
  if (chromeNudged || isDisplayFullscreen()) return;
  chromeNudged = true;
  try {
    window.scrollTo(0, 1);
    requestAnimationFrame(() => window.scrollTo(0, 0));
  } catch {
    // ignore
  }
}

/** Keep layout boxes matched to the visual viewport as the address bar shows or hides. */
export function bindVisualViewportFill(): void {
  const apply = (): void => {
    const { w, h } = viewportSize();
    const root = document.documentElement;
    root.style.setProperty('--vvw', `${w}px`);
    root.style.setProperty('--vvh', `${h}px`);
  };
  apply();
  window.visualViewport?.addEventListener('resize', apply);
  window.visualViewport?.addEventListener('scroll', apply);
  window.addEventListener('resize', apply);
}
